/**
 * How people look next to their names (amendment 21): whose picture is there, and which frame
 * they wear — asked of the server for the names on screen, a batch at a time, and kept for a
 * while, so that a chat or a leaderboard costs one small request rather than one per row.
 *
 * The pictures themselves are fetched rather than linked: the APK signs in with a bearer token
 * an `<img>` would not send, and a face is not something to hand to anybody who asks.
 */

import type { Look } from '$domain/looks.ts';
import { request, requestBlob } from './api.ts';

/** A look this old is asked for again: somebody may have bought a frame in the meantime. */
const STALE_MS = 10 * 60 * 1000;
/** Names wanted within this long go out together. */
const BATCH_MS = 60;
/** The server's own limit per request. */
const BATCH_MAX = 100;

class Looks {
  #looks = $state<Record<string, Look & { at: number }>>({});
  #pictures = $state<Record<string, string>>({});
  #wanted = new Set<string>();
  #loading = new Set<string>();
  #timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * How `username` looks, as far as this device knows — and, when it does not know or not
   * lately, a request for it, which arrives as a change. Safe to call while drawing: nothing
   * reactive changes until the answer is in.
   */
  of(username: string): Look | undefined {
    const known = this.#looks[username];
    if (known === undefined || Date.now() - known.at > STALE_MS) this.#want(username);
    return known;
  }

  /** `username`'s picture at `version`, as a URL to show, once it has arrived. */
  picture(username: string, version: number): string | undefined {
    const key = `${username}@${version}`;
    const url = this.#pictures[key];
    if (url === undefined && !this.#loading.has(key)) {
      this.#loading.add(key);
      void this.#fetch(username, version, key);
    }
    return url;
  }

  /** What this device just changed about `username`'s look, without waiting for a read. */
  remember(username: string, change: Partial<Look>): void {
    const known = this.#looks[username] ?? { avatar: null, frame: null, accessory: null };
    this.#looks = { ...this.#looks, [username]: { ...known, ...change, at: Date.now() } };
  }

  #want(username: string): void {
    this.#wanted.add(username);
    this.#timer ??= setTimeout(() => void this.#flush(), BATCH_MS);
  }

  async #flush(): Promise<void> {
    this.#timer = undefined;
    const names = [...this.#wanted].slice(0, BATCH_MAX);
    for (const name of names) this.#wanted.delete(name);
    if (this.#wanted.size > 0) this.#timer = setTimeout(() => void this.#flush(), BATCH_MS);
    try {
      const { looks } = await request<{ looks: Record<string, Look> }>(
        `/api/looks?u=${names.map(encodeURIComponent).join(',')}`,
      );
      const at = Date.now();
      const next = { ...this.#looks };
      for (const name of names) {
        next[name] = { ...(looks[name] ?? { avatar: null, frame: null, accessory: null }), at };
      }
      this.#looks = next;
    } catch {
      // Offline, or the server said no: the emoji stays, and the next look asks again.
    }
  }

  async #fetch(username: string, version: number, key: string): Promise<void> {
    try {
      const blob = await requestBlob(`/api/avatars/${encodeURIComponent(username)}?v=${version}`);
      // An older picture of the same person is not shown again: its URL goes, and its memory.
      const next: Record<string, string> = { [key]: URL.createObjectURL(blob) };
      for (const [other, url] of Object.entries(this.#pictures)) {
        const [name, older] = other.split('@');
        if (name === username && Number(older) < version) URL.revokeObjectURL(url);
        else next[other] = url;
      }
      this.#pictures = next;
    } catch {
      // Gone in the meantime, or offline: the emoji stands in for it.
    } finally {
      this.#loading.delete(key);
    }
  }
}

export const looks = new Looks();
